import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import { isPolygonValid } from "utility/geometryUtils";
import { ClickAwayListener, List, ListItemButton, ListItemText } from '@mui/material';
import { mapFilterList } from 'utility/constants';
import MyVirtualizedList from 'shared-component/virtual-list-component';
import { disableListItemBtn, getLatLongPositionData, getMapRoadData, getSelectedList, getZipCodes, getZipCodesCity, getZipCodesCityWithStateName } from 'utility/helper';
import { useLocation } from 'react-router-dom';
import { MapCityFinder } from './components/MapCityFinder';

const customIcon = L.divIcon({
  className: 'custom-icon',
  html: '<div />',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  popupAnchor: [0, -6],
});

const icon = L.icon({
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  iconSize: [20, 35],
  iconAnchor: [12, 35],
});

interface Point {
  latitude: number;
  longitude: number;
}


export interface MapRevisedProps {
  points: Point[];
  onCoordinateChange: Function;
  coordinatePanel: Function;
  mapPosition: any;
  showPanel: boolean,
  selectionOption: string,
  setSelectedOption: (value: any) => void,
  filterData: any,
  mapData: any,
  showRangeButtons?: boolean;
  showLatLongEntry?: boolean;
  allowMarkerCreation?: boolean;
}

const defaultCityZip = [{state: null, city: null}];

const MapComponentRevised: React.FC<MapRevisedProps> = ({
  points,
  onCoordinateChange,
  coordinatePanel,
  showPanel,
  selectionOption,
  mapPosition,
  setSelectedOption,
  filterData,
  mapData,
  showRangeButtons = true,
  showLatLongEntry = true,
  allowMarkerCreation = true,
}) => {
  const latLongRef = useRef(null); // Ref for the button
  const withInRangeRef = useRef(null); // Ref for the button
  const outsideRangeRef = useRef(null); // Ref for the button
  const filterByRef = useRef(null); // Ref for the button
  const dropDownRef = useRef(null);
  const filterSelectedRef = useRef(null);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const mapRef = useRef<L.Map | null>(null);
  const [clicks, setClicks] = useState(0);
  const polygonRef = useRef<L.Polygon | null>(null);
  const polylineRef = useRef<L.Polyline | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const originalPositionsRef = useRef<L.LatLng[]>([]);
  const [filterSelected, setFilterSelected] = useState<any>("")
  const [filterLabel, setFilterLabel] = useState("");
  const [mapPositionData, setMapPositionData] = useState<any>(mapPosition);
  const [listItems, setListItem] = useState<any>([])
  const routeLocation = useLocation();
  const [cityState, setCityState] = useState<any[]>(defaultCityZip);
  const [stateCodeValue, setStateCode] = useState<string[]>([]);
  const { id } = routeLocation.state;
  const [roadMapDataList, setRoadMapDataList] = useState<any>([])
  console.log('id', id)
  const ClusteredMarkers = () => {
    const map = useMap();

    useEffect(() => {
      const clusterGroup = L.markerClusterGroup();

      mapPositionData?.forEach((marker: any) => {
        if(marker.data) {
          const newMarker = L.marker(marker.position, { icon: customIcon }).bindPopup(JSON.stringify(marker.data));
          clusterGroup?.addLayer(newMarker);
        }
      });

      map.addLayer(clusterGroup);

      // Clean up on unmount
      return () => {
        map.removeLayer(clusterGroup);
      };
    }, [map]);

    return null; // This component doesn't render anything
  };

  const [subFilterSelection, setSubFilterSelection] = useState<any>(null);

  const coordinateChange = useCallback(
    (lat: number, lng: number, index: number) => {
      onCoordinateChange([lat, lng], index);
    },
    [onCoordinateChange]
  );

  useEffect(() => {
    if (mapRef.current && points.length > 0) {
      markersRef.current.forEach((marker) => {
        mapRef.current?.removeLayer(marker);
      });

      markersRef.current = [];
      originalPositionsRef.current = [];

      points.forEach(({ latitude, longitude }, index) => {
        if (
          !isNaN(latitude) &&
          !isNaN(longitude) &&
          latitude !== null &&
          longitude !== null
        ) {
          const marker = L.marker([latitude, longitude], {
            icon,
            draggable: true,
          }).addTo(mapRef.current!);

          marker.bindTooltip(`${index + 1}`);
          marker.on("dragstart", () => {
            originalPositionsRef.current[index] = marker.getLatLng();
          });

          marker.on("dragend", function (e) {
            const lat = e.target.getLatLng().lat;
            const lng = e.target.getLatLng().lng;
            const markerIndex = markersRef.current.indexOf(marker);

            const tempPoints = markersRef.current.map(m => m.getLatLng());
            tempPoints[markerIndex] = L.latLng(lat, lng);

            if (isPolygonValid(tempPoints)) {
              coordinateChange(lat, lng, markerIndex);
              updatePolygon();
              updatePolyline();
            } else {
              marker.setLatLng(originalPositionsRef.current[markerIndex]);
              if (window.confirm("Invalid selection, click okay to revert the marker")) {
                marker.setLatLng(originalPositionsRef.current[markerIndex]);
              }
            }
          });

          markersRef.current.push(marker);
          originalPositionsRef.current.push(marker.getLatLng());
        }
      });

      if (markersRef.current.length > 0) {
        const markerBounds = L.latLngBounds(
          markersRef.current.map((marker) => marker.getLatLng())
        );
        mapRef.current?.fitBounds(markerBounds);
      }
    }
  }, [points, coordinateChange]);

  const checkCoord = (bound: string) => {
    if (mapRef.current && markersRef.current) {

      // Get the bounds of the polygon
      const bounds = polygonRef.current && polygonRef.current.getBounds();

      //Check which markers fall within the defined area
      if (bound === 'in_bond') {
        mapPositionData.filter((marker: any) => {
          const latLng = L.latLng(marker.position);
          return bounds && bounds.contains(latLng); // Check if the marker is within the bounds
        });
      }
      if (bound === 'out_bond') {
        mapPositionData.filter((marker: any) => {
          const latLng = L.latLng(marker.position);
          return bounds && !bounds.contains(latLng); // Check if the marker is within the bounds
        });
      }
    }
  }

  useEffect(() => {
    const handleMapClick = (e: L.LeafletMouseEvent) => {
      if (!allowMarkerCreation) return;

      const clickedElement = e?.originalEvent?.srcElement;
      const isButtonClicked = [latLongRef?.current, withInRangeRef?.current, outsideRangeRef?.current, filterByRef?.current, dropDownRef?.current, filterSelectedRef?.current].some((ref: any) => ref && ref?.contains(clickedElement));
      if (!isButtonClicked) {
        const currentZoom = mapRef.current?.getZoom();
        if (markersRef.current.length < 4) {
          const tempPoints = [...markersRef.current.map(m => m.getLatLng()), e.latlng];
          if (isPolygonValid(tempPoints)) {
            const incrementedClicks = clicks + 1;
            setClicks(incrementedClicks);
            coordinateChange(e.latlng.lat, e.latlng.lng, markersRef.current.length);

            const marker = L.marker(e.latlng, {
              icon: icon,
              draggable: true,
            }).addTo(mapRef.current!);

            marker.bindTooltip(`${markersRef.current.length + 1}`);
            marker.on("dragstart", () => {
              originalPositionsRef.current[markersRef.current.length] = marker.getLatLng();
            });

            marker.on("dragend", function (e) {
              const lat = e.target.getLatLng().lat;
              const lng = e.target.getLatLng().lng;
              const markerIndex = markersRef.current.indexOf(marker);

              const tempPoints = markersRef.current.map(m => m.getLatLng());
              tempPoints[markerIndex] = L.latLng(lat, lng);

              if (isPolygonValid(tempPoints)) {
                coordinateChange(lat, lng, markerIndex);
                updatePolygon();
                updatePolyline();
              } else {
                marker.setLatLng(originalPositionsRef.current[markerIndex]);
                if (window.confirm("Invalid selection, click okay to revert the marker")) {
                  marker.setLatLng(originalPositionsRef.current[markerIndex]);
                }
              }
            });

            markersRef.current.push(marker);
            originalPositionsRef.current.push(marker.getLatLng());

            if (markersRef.current.length === 4) {
              updatePolygon();
            }

            if (markersRef.current.length > 1) {
              updatePolyline();
            }

            if (currentZoom !== undefined) {
              setTimeout(() => {
                mapRef.current?.setZoom(currentZoom);
              }, 1000);
            }
          } else {
            alert("Invalid selection, click okay to revert the marker");
          }
        }
      }
    };

    if (mapRef.current) {
      mapRef.current.on("click", handleMapClick);
    }
    const currentMap = mapRef.current;
    return () => {
      if (currentMap) {
        currentMap.off("click", handleMapClick);
      }
    };
  }, [clicks, coordinateChange, allowMarkerCreation]);

  useEffect(() => {
    if (mapRef.current) {
      updatePolygon();
      updatePolyline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markersRef.current]);

  const updatePolygon = () => {
    if (polygonRef.current) {
      mapRef.current?.removeLayer(polygonRef.current);
    }
    polygonRef.current = L.polygon(
      markersRef.current.map((marker) => marker.getLatLng()),
      {
        color: "#e12525",
        fillOpacity: 0.5,
      }
    ).addTo(mapRef.current!);
  };

  const updatePolyline = () => {
    if (polylineRef.current) {
      mapRef.current?.removeLayer(polylineRef.current);
    }
    polylineRef.current = L.polyline(
      markersRef.current.map((marker) => marker.getLatLng()),
      {
        color: "#e12525",
      }
    ).addTo(mapRef.current!);
  };

  const handleItemSelect = (item: string, label: string) => {
    if (subFilterSelection && subFilterSelection.hasOwnProperty(label)) {
      if (subFilterSelection[label]?.includes(item)) {
        setSubFilterSelection((prevState: any) => ({
          ...prevState,
          [label]:  prevState[label].filter((i: string) => i !== item)
        }))
      } else {
        setSubFilterSelection((prevState: any) => ({
          ...prevState,
          [label]: [...prevState[label], item]
        }))
      }
    } else if (subFilterSelection && !subFilterSelection.hasOwnProperty(label)) {
      setSubFilterSelection({
        ...subFilterSelection,
        [label]: [item]
      })
    } else {
      setSubFilterSelection({
        [label]: [item]
      })
    }
  };

  const handleFilterSelection = () => {
    setCityState(defaultCityZip)
    if (filterSelected?.length > 0 && subFilterSelection && filterSelected !== "resetSelection") {
      let zipCodesList: string[] = [];
      let maxSpeed: string [] = [];
      let surface: string [] = [];
      if (subFilterSelection && subFilterSelection.Zipcode && subFilterSelection?.hasOwnProperty('Zipcode') && subFilterSelection?.Zipcode?.length > 0) zipCodesList.push(...(subFilterSelection?.Zipcode || []))
      if (subFilterSelection?.hasOwnProperty('State') && subFilterSelection?.State?.length > 0) {
        const data: any = getZipCodes(subFilterSelection?.State)
        zipCodesList.push(...data);
      }
      if (subFilterSelection?.hasOwnProperty('City') && subFilterSelection?.City?.length > 0) {
        const data: any = getZipCodesCity(subFilterSelection?.City)
        zipCodesList.push(...data);
      }
      if (subFilterSelection?.hasOwnProperty('Speed') && subFilterSelection?.Speed?.length > 0) {
        maxSpeed = subFilterSelection?.Speed;
      }
      if (subFilterSelection?.hasOwnProperty('Surface') && subFilterSelection?.Surface?.length > 0) {
        surface = subFilterSelection?.Surface;
      }
      const maxSpeedData = maxSpeed?.length > 0 ? { maxspeed: maxSpeed } : false;
      const surfacePayload = surface?.length > 0 ? { surface: surface } : false;
      const zipCodeData = zipCodesList?.length > 0 ? { "zip_code": zipCodesList } : false;
      console.log(maxSpeedData, surfacePayload, zipCodeData)
      setFilterSelected('');
      setShowFilter(false);
    }
  }

  useEffect(() => {
    //const updatedData = getFilterSelectedGeoLocationData(mapData, selection);
    const mapFilterPoints = getLatLongPositionData(mapData);
    const roadMapData = getMapRoadData(mapData);
    setRoadMapDataList(roadMapData)
    setMapPositionData(mapFilterPoints);
    if (mapRef.current && mapFilterPoints?.length > 0 && (!cityState![0]?.city && !cityState![0]?.state)) mapRef.current.flyTo(mapFilterPoints[0].position, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData])

  useEffect(() => {
    if (filterSelected === "resetSelection") {
      resetSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected])

  const resetSelection = () => {
    const updateData = getLatLongPositionData(mapData);
      setMapPositionData(updateData);
      setFilterSelected('');
      setFilterLabel('');
      setSubFilterSelection(null)
      setShowFilter(false);
      if (mapRef.current) mapRef.current.flyTo([37.8, -96], 4);
  }

  useEffect(() => {
    if (showFilter && mapRef.current) {
      mapRef.current.scrollWheelZoom.disable();
    }
    if (!showFilter && mapRef.current) {
      mapRef.current.scrollWheelZoom.enable();
    }
  }, [showFilter, mapRef]);

  useEffect(() => {
    if (filterSelected) {
      setListItem(getSelectedList(filterSelected, subFilterSelection))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelected])

  useEffect(() => {
    let zipCodesList: string[] = [];
    if (cityState?.length > 0) {
      const data: any = cityState?.length < 10 ?  getZipCodesCityWithStateName(cityState, stateCodeValue) : [];
      zipCodesList.push(...data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityState, stateCodeValue])

  return (
    <div>
      <MapContainer center={[37.8, -96]} zoom={4} minZoom={1} style={{ height: '500px', width: '100%' }} ref={mapRef}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {roadMapDataList?.length > 0 &&
          roadMapDataList?.map((line: any) => (
            <Polyline positions={line?.geometry} color="blue" weight={4}>
              <Tooltip permanent>{line?.toolTip}</Tooltip>
            </Polyline>
          ))}
        <MapCityFinder setCityState={setCityState} setStateCode={setStateCode} />
        <ClusteredMarkers />
        {showLatLongEntry && (
          <button
            onClick={(e) => { coordinatePanel(!showPanel); }}
            ref={latLongRef}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              padding: '10px',
              backgroundColor: 'white',
              border: '2px solid #a19e9e',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: "bold",
              zIndex: 1000, // Ensure it is above the map
            }}
          >
            Lat / long Entry
          </button>
        )}

        {showRangeButtons && !showPanel && <button
          onClick={(e) => { e.stopPropagation(); setSelectedOption('in_bond'); checkCoord('in_bond'); }}
          ref={withInRangeRef}
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            padding: '10px',
            backgroundColor: selectionOption === 'in_bond' ? 'red' : 'white',
            color: selectionOption === 'in_bond' ? 'white' : 'black',
            border: '2px solid #a19e9e',
            borderRadius: '5px',
            cursor: 'pointer',
            fontWeight: "bold",
            zIndex: 1000, // Ensure it is above the map
          }}
        >
          WithIn Range
        </button>}
        {showRangeButtons && !showPanel && <button
          onClick={(e) => { setSelectedOption('out_bond'); checkCoord('out_bond'); }}
          ref={outsideRangeRef}
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '120px',
            padding: '10px',
            backgroundColor: selectionOption === 'out_bond' ? 'red' : 'white',
            color: selectionOption === 'out_bond' ? 'white' : 'black',
            border: '2px solid #a19e9e',
            borderRadius: '5px',
            cursor: 'pointer',
            fontWeight: "bold",
            zIndex: 1000, // Ensure it is above the map
          }}
        >
          Outside Range
        </button>}
        {filterSelected?.length > 0 && showFilter && filterSelected !== "resetSelection" &&
        <ClickAwayListener onClickAway={() => {resetSelection()}}>
          <div
            ref={filterSelectedRef}
            style={{
              position: 'absolute',
              bottom: '106px',
              right: '194px',
              padding: '10px',
              backgroundColor: 'white',
              border: '2px solid #a19e9e',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold',
              zIndex: 1000, // Ensure it is above the map
              width: "50%"
            }}
            id="filterSelected"
          >
            {filterSelected !== "resetSelection" ? <MyVirtualizedList items={listItems} filterLabel={filterLabel} onItemSelect={handleItemSelect} filterSelected={filterSelected} subFilterSelection={subFilterSelection} handleFilterSelection={handleFilterSelection} /> : null}
          </div>
          </ClickAwayListener>}
        {showFilter &&
          <div
            ref={dropDownRef}
            style={{
              position: 'absolute',
              bottom: '48px',
              right: '24px',
              padding: '10px',
              backgroundColor: 'white',
              border: '2px solid #a19e9e',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: "bold",
              zIndex: 1000, // Ensure it is above the map
              width: "37%"
            }}
            id="filterDropDown"
          >
            <List
              sx={{
                width: '100%', maxWidth: 360, bgcolor: 'background.paper'
              }}
            >
              {mapFilterList?.map((item: any) => (
                <ListItemButton sx={{
                  '&:hover': {
                    backgroundColor: 'red', // Change background color to red on hover
                    color: 'white', // Change text color to white for better contrast
                  },
                  backgroundColor: filterSelected === item?.value ? "#ff6c30" : '',
                  height: "24px"
                }}
                  onClick={(e) => {e?.preventDefault(); e?.stopPropagation(); setFilterSelected(item?.value); setFilterLabel(item?.label)}}
                  disabled={disableListItemBtn(item, subFilterSelection)}
                >
                  <ListItemText primary={item?.label} />
                </ListItemButton>
              ))
              }
            </List>
          </div>}
        <button
          onClick={() => { setShowFilter(!showFilter); }}
          ref={filterByRef}
          style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            padding: '10px',
            backgroundColor: 'white',
            border: '2px solid #a19e9e',
            borderRadius: '5px',
            cursor: 'pointer',
            fontWeight: "bold",
            zIndex: 1000, // Ensure it is above the map
          }}
        >
          Filter By
        </button>
      </MapContainer>

    </div>
  );
};

export default MapComponentRevised;
